import React, { useMemo } from 'react';
import PropTypes, { func } from 'prop-types';
import {
  Body1Stronger,
  Combobox,
  makeStyles,
  mergeClasses,
  Option,
  tokens,
  useId,
} from '@fluentui/react-components';

import { fontFamilies } from '../constants';

const useFontsSelectorStyles = makeStyles({
  fontSelectWrapper: {
    position: 'relative',
    marginTop: tokens.spacingVerticalS,
  },
  fontSelect: {
    minWidth: '280px',
    width: '100%',
    '& > input::selection': {
      backgroundColor: 'transparent',
    },
  },
  fontSelectValue: {
    position: 'absolute',
    top: '50%',
    left: '12px',
    width: 'auto',
    height: '20px',
    zIndex: 1,
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    '@media (forced-colors: active)': {
      filter: 'invert(1)',
    },
  },
  fontSelectInputVisible: {
    '& > input': {
      color: 'inherit',
    },
  },
  fontSelectInputTransparent: {
    '& > input': {
      fontSize: '0',
      color: 'transparent',
    },
  },
  fontSelectOption: {
    '& img': {
      '@media (forced-colors: active)': {
        filter: 'invert(1)',
      },
    },
  },
});

export const FontsFamilySelector = ({
  id,
  i18n,
  label,
  value,
  ariaLabel,
  onChange,
  className,
  inlinePopup,
  placeholder,
  selectClassName,
  overrideFontFamilies,
}) => {
  const classes = useFontsSelectorStyles();
  const labelId = useId('font-selector-label');
  const sanitizeLabel = inputLabel => inputLabel.replace(/[^a-zA-Z0-9-_]/g, '');
  const fontSelectId = `${sanitizeLabel(label || 'family')}-font-select`;

  const fontFamilyOptions = overrideFontFamilies || fontFamilies;

  const selectedFontSvg = useMemo(() => {
    const selectedFont =
    fontFamilyOptions.find(family => family.key === value) || null;
    return selectedFont ? selectedFont.svg : null;
  }, [value, fontFamilyOptions]);

  const handleFontChange = (_ev, data) => {
    if (data && data.optionValue) {
      onChange(data.optionValue);
    }
  };

  return (
    <div className={mergeClasses(`${id}-font-selector`, 'ads-fonts-family-selector', className)}>
      {label ? (
        <Body1Stronger id={labelId} aria-label={ariaLabel || label}>{label}</Body1Stronger>
      ) : (
        <Body1Stronger id={labelId} aria-label={ariaLabel || i18n.getString(_TL_('Select font'))} style={{ display: 'none' }}>
          {ariaLabel || i18n.getString(_TL_('Select font'))}
        </Body1Stronger>
      )}
      <div className={classes.fontSelectWrapper}>
        {selectedFontSvg ? (
          <img
            alt={value}
            aria-hidden="true"
            src={selectedFontSvg}
            className={classes.fontSelectValue}
          />
        ) : null}
        <Combobox
          freeform={false}
          id={fontSelectId}
          value={value}
          inlinePopup={inlinePopup}
          selectedOptions={[value]}
          onOptionSelect={handleFontChange}
          aria-labelledby={labelId}
          placeholder={placeholder || i18n.getString(_TL_('Select font'))}
          className={mergeClasses(
            `${id}-font-select`,
            'ads-fonts-family-select',
            classes.fontSelect,
            selectedFontSvg
              ? classes.fontSelectInputTransparent
              : classes.fontSelectInputVisible,
            selectClassName
          )}
        >
          {fontFamilyOptions.map(family => (
            <Option
              key={family.key}
              text={family.key}
              value={family.key}
              aria-label={family.text}
              id={family.key.replace(/\s/g, '-').toLowerCase()}
              className={mergeClasses('ads-fonts-family-select-option', classes.fontSelectOption)}
            >
              <img src={family.svg} alt={family.text} />
            </Option>
          ))}
        </Combobox>
      </div>
    </div>
  );
};

FontsFamilySelector.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  selectClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  overrideFontFamilies: PropTypes.arrayOf({
    key: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    svg: PropTypes.string.isRequired,
  }),
  inlinePopup: PropTypes.bool,
  placeholder: PropTypes.string,
};

FontsFamilySelector.defaultProps = {
  label: '',
  ariaLabel: null,
  className: '',
  selectClassName: '',
  overrideFontFamilies: null,
  inlinePopup: false,
  placeholder: null,
};
